import { Maybe } from "@graphql-tools/utils";
import {
	AttributeIdentifiers,
	EligibilityConditionCode,
	EligibilityStatus,
	FlexFinancialDataPointSnapshot,
} from "@lyfegen-platform/graphql-schema/models";
import { Loading } from "@lyfegen-platform/react-common";
import { byAsc, getTreatmentCodeByCountry, parseDate, sortBy, sortBySelectors } from "@lyfegen-platform/utils";
import { Box, Typography } from "@material-ui/core";
import { addDays, differenceInDays, format, isBefore, startOfDay } from "date-fns";
import i18next from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSetRecoilState } from "recoil";
import MappedComponent from "../../../components/mapped-components/MappedComponent";
import NotFound from "../../../components/NotFound";
import SubscriptionForUpdates from "../../../hooks/subscription-for-updates";
import useLocationQuery from "../../../hooks/use-location-query";
import { useDataQuery } from "../../../hooks/useDataModule";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../models/common/data";
import { CatalogueAttributeType } from "../../../models/flexible/catalogue-attribute-type";
import { fieldControllerFactory } from "../../../models/flexible/field-controller-factory";
import { FlexEntitySchema } from "../../../models/flexible/flex-entity-schema";
import { TypedFlexEntity } from "../../../models/flexible/typed-flex-entity";
import { TypedNegotiationRound } from "../../../models/flexible/typed-negotiation-round";
import {
	BaseAgreement,
	Case,
	CaseStatus,
	CatalogueAttribute,
	ConditionConsideredRating,
	ConditionType,
	ConditionType as ScenarioConditionType,
	DocumentStatus,
	EnrollingConditionSet,
	FlexContainerType,
	FlexEntityFeature,
	FlexFormStatus,
	NegotiationRoundStatus,
	Price,
	PriceCategory,
	RefundStatus,
	ScenarioCondition,
	Treatment,
} from "../../../models/generated/graphql";
import { caseAgreementOrganizationRole } from "../../../store/cases";
import { flexibleFormRelatedAgreements } from "../../../store/flexible";
import {
	combineIgnoredAndMissingAttributes,
	getPreparationsWithDistinguishBrands as getStartingPointPreparations,
	isConditionSetMatched,
	isDateMatchedByTimeframe,
} from "../../../utils/agreement-utils";
import { anyNamedEntity } from "../../../utils/any-named-entity";
import {
	formatDiseases,
	formatICondition,
	formatIndications,
	formatPreparation,
	formatTreatment,
	getPreparations,
	indicationDisplay,
	notAvailableKey,
} from "../../../utils/base-agreement-formatters";
import renderCurrency from "../../../utils/currency";
import { formatDataPoint } from "../../../utils/data-point-formatter";
import { distinctBy } from "../../../utils/distinct-by";
import { flattenFlexEntitySection } from "../../../utils/flatten-flex-entity-data";
import { groupBy } from "../../../utils/group-by";
import { isEmptyValue } from "../../../utils/is-empty-value";
import { neo4jDateToDate } from "../../../utils/neo4j-date-to-date";
import { sumBy } from "../../../utils/sum-by";
import { TimeframeEvaluator, TimeframeParamsType } from "../../../utils/timeframe-evaluator";
import { TrFun } from "../../../utils/types";
import { RECORDS_SUBSCRIPTION } from "../../data/queries";
import { GET_CASE_BY_ID } from "../queries";
import { GetCaseByIdQuery } from "../types/queries.generated";
import Body from "./Body";
import RightPermanentDrawer from "./Drawers/RightPermanentDrawer";
import { CaseFormStatus, IFormsCard } from "./FormCard";
import Header from "./Header";
import { ConditionArea, ConditionState, ICondition } from "./RecordsCard";
import { useSetCaseBreadcrumbs } from "./use-set-case-breadcrumbs";

export const PLACEHOLDER_XX = "XX";

export type ConditionEvaluationResult = {
	scenarioOrder: number;
	conditionOrder: number;
	certainty: "certain" | "possible" | "failed" | "not-relevant";
};

type PatientEligibilityCode = { code: EligibilityConditionCode; response: boolean | "uncertain" };
type PatientEligibilityCodes = Array<PatientEligibilityCode>;

interface ConditionStateProps {
	relevantBaseAgreementVersion: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>;
	initialAgreementVersion: GetCaseByIdQuery["cases"][number]["baseAgreement"];
	snapshotObject: FlexFinancialDataPointSnapshot;
	forms: IFormsCard[][];
	prescriptionDate: Date;
}

export interface IAttribute {
	attribute: CatalogueAttribute;
	value: any;
	position: number;
}

type AgreementVersionType = Pick<
	NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>,
	"validFromDate" | "validToDate" | "treatmentPricesConnection"
>;

const today = startOfDay(new Date());

function getFormStatus(timeframe: Maybe<[Date | null | undefined, Date | null | undefined]>): CaseFormStatus {
	if (timeframe?.[1] && isBefore(timeframe[1], today)) {
		return "overdue";
	}

	if ((timeframe?.[0] && isBefore(timeframe[0], today), today)) {
		return "due";
	}

	return undefined;
}

function createFormDataByDataGuidance(
	caseEntity: GetCaseByIdQuery["cases"][number] & {
		flexEntities: Array<GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]["node"]>;
	}
) {
	const createdFormTypes = new Set(caseEntity.flexEntities?.map((entity) => entity.type.id));
	const dataContainers =
		caseEntity.baseAgreement?.dataContainers.filter(
			(container) =>
				container.type === FlexContainerType.Form && !container.isStartingPoint && !createdFormTypes.has(container.subType)
		) || [];
	const timeframeEvaluator = new TimeframeEvaluator(neo4jDateToDate(caseEntity.baseAgreement?.validFromDate)!);
	const startingPoint = caseEntity.flexEntitiesConnection.edges.find(({ isStartingPoint }) => isStartingPoint)!.node;

	const forms: Array<IFormsCard> = dataContainers
		.map((dataContainer) => ({
			dataContainer,
			timeframe:
				dataContainer.timeframeStartingPoint &&
				timeframeEvaluator.getTimeframe(
					dataContainer as unknown as TimeframeParamsType,
					caseEntity.flexEntities as unknown as TypedFlexEntity<any>[]
				),
		}))
		.sort(sortBySelectors((s) => s.timeframe?.[1] || s.timeframe?.[0]))
		.map((tuple) => ({
			caseId: caseEntity.id,
			patientAlias: (startingPoint.data as any)[AttributeIdentifiers.PatientId],
			typeId: tuple.dataContainer.subType,
			title: tuple.dataContainer.entityTypeName,
			timeLimit: tuple.timeframe?.[1] || tuple.timeframe?.[0],
			isTimeLimitPassed:
				(tuple.timeframe?.[1] || tuple?.timeframe?.[0]) && isBefore(tuple.timeframe?.[1] || (tuple?.timeframe?.[0] as Date), today),
			timeframeRelation: tuple.dataContainer.timeframeRangeType ?? undefined,
			isFormOwner: caseEntity?.isFormOwner ?? undefined,
			isCreatable: true,
			caseFormStatus: getFormStatus(tuple.timeframe),
			occurrence: null,
			addedBy: null,
			isEditingFeature: false,
			lastUpdatedOn: null,
			isAddedByEnabledInTable: false,
		}));

	return forms;
}

function createFormCardData(caseInstance: GetCaseByIdQuery["cases"][number]): Array<Array<IFormsCard>> {
	const allForms: Array<IFormsCard> = [...caseInstance.flexEntitiesConnection.edges]
		.sort(sortBySelectors(({ node: flexEntity }) => flexEntity.lastUpdatedOn))
		.map(({ isStartingPoint, node: flexEntity }) => ({
			caseId: caseInstance.id,
			occurrence: flexEntity.occurrence ?? null,
			addedBy: flexEntity.createdBy?.tenantId === caseInstance.sourceTenantId ? flexEntity.createdBy : null,
			isAddedByEnabledInTable:
				flexEntity.type.catalogueAttributesConnection.edges.some(
					(edge) => edge.node.identifier === AttributeIdentifiers.AddedBy && edge.isDisplayedInCaseTable
				) ?? false,
			formId: flexEntity.id,
			typeId: flexEntity.type.id,
			entity: flexEntity as GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]["node"],
			title: flexEntity.type.localizationKey,
			isEditingFeature: flexEntity.type.features.includes(FlexEntityFeature.Editing),
			status: flexEntity.status,
			submissionDate:
				flexEntity.status === FlexFormStatus.Submitted ? neo4jDateToDate(flexEntity.lastUpdatedOn ?? flexEntity.createdOn) : null,
			lastUpdatedOn: neo4jDateToDate(flexEntity.lastUpdatedOn),
			timeLimit: undefined,
			isTimeLimitPassed: false,
			isCreatable: false,
			isFormOwner: caseInstance.isFormOwner ?? false,
			isDeletionEnabled: flexEntity.type.features.includes(FlexEntityFeature.Deletion),
			caseFormStatus: flexEntity.status === FlexFormStatus.Submitted ? "submitted" : undefined,
			casesCount: flexEntity.cases.length,
			caseNonStartingPointForms: caseInstance.flexEntitiesConnection.edges.filter(
				({ isStartingPoint: isFormStartingPoint, node: form }) => form.id !== flexEntity.id && !isFormStartingPoint
			),
			isStartingPoint,
		}));

	return Array.from(groupBy(allForms, (nf) => nf.entity?.type.id).values());
}

function getEligibilityConditionState(value: Maybe<EligibilityStatus>): ConditionState {
	if (isEmptyValue(value) || value === EligibilityStatus.UNCERTAIN) {
		return "pending";
	}

	return value === EligibilityStatus.ELIGIBLE ? "matched" : "unmatched";
}

function getSnapshotConditionState(condition: ConditionEvaluationResult): ConditionState {
	switch (condition.certainty) {
		case "certain":
			return "matched";
		case "possible":
			return "pending";
		case "failed":
			return "unmatched";
		case "not-relevant":
			return "not-relevant";
		default:
			return "pending";
	}
}

const getResponse = (response: boolean | "uncertain") => {
	if (response === "uncertain") {
		return "pending";
	}
	if (response === true) {
		return "matched";
	}
	if (response === false) {
		return "unmatched";
	}
	throw new Error("getResponse received unexpected value");
};

function getBaseAgreementEligibilityConditions(
	agreement: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>,
	eligibilityConditionTreatments: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]["eligibilityConditionTreatments"]>,
	snapshotObject: FlexFinancialDataPointSnapshot,
	t: TrFun
) {
	return agreement.eligibilityConditionSetsConnection.edges
		.map((condition) => {
			if (condition.node.code === EligibilityConditionCode.NUMBER_OF_PRESCRIPTIONS) {
				const props = agreement.eligibilityConditionSetsConnection.edges.find((v) => v.numberOfAdministeredRangeDays);
				const numberOfAdministeredRangeDays = props?.numberOfAdministeredRangeDays || [0, 0];
				const eligibilityResponseIndex = (snapshotObject?.eligibilityCodes as PatientEligibilityCodes)?.findIndex(
					(eligibility) => eligibility.code === EligibilityConditionCode.NUMBER_OF_PRESCRIPTIONS
				);
				const response =
					eligibilityResponseIndex >= 0 &&
					(snapshotObject.eligibilityCodes[eligibilityResponseIndex] as PatientEligibilityCode).response;

				return {
					name: "NUMBER_OF_PRESCRIPTIONS",
					value: i18next.t(`NUMBER_OF_PRESCRIPTIONS_VALUE`, {
						numberOf: t("number_of"),
						treatments: eligibilityConditionTreatments?.map((t) => ` ${t?.GTIN} - ${t?.Name} `).filter(Boolean),
						dispensedRange: t("dispensed_within_the_fixed_range_of"),
						dispensedRangeValue: `${numberOfAdministeredRangeDays[0]} - ${numberOfAdministeredRangeDays[1]}`,
						days: t(`days`),
						startingFromDisDate: t("starting_from_first_administration_date"),
						operator: t(props?.numberOfAdministeredTreatmentsOperator || ""),
						numberOfAdministeredTreatments: props?.numberOfAdministeredTreatments,
					}),
					state: getResponse(response),
					conditionType: ConditionArea.ELIGIBILITY,
				} as any;
			}
			if (condition.node.code === EligibilityConditionCode.TEST_THRESHOLD) {
				const props = agreement.eligibilityConditionSetsConnection.edges.find((condition) => condition.referenceTestRangeDays);
				const referenceTestRangeDays = props?.referenceTestRangeDays || [0, 0];
				const eligibilityResponseIndex = (snapshotObject?.eligibilityCodes as PatientEligibilityCodes)?.findIndex(
					(eligibility) => eligibility.code === EligibilityConditionCode.TEST_THRESHOLD
				);
				const response =
					eligibilityResponseIndex >= 0 &&
					(snapshotObject.eligibilityCodes[eligibilityResponseIndex] as PatientEligibilityCode).response;

				return {
					name: "TEST_THRESHOLD",
					value: i18next.t("TEST_THRESHOLD_VALUE", {
						dateOfTestNumber: t("dateOfTestNumber"),
						testNumber: props?.previousTestNumber,
						isWithin: t("is_within"),
						rangeDays: `${referenceTestRangeDays[0]} - ${referenceTestRangeDays[1]}`,
						daysAfter: t("days_after_dispensing_number"),
						referenceAdministrationSequenceNumber: props?.referenceAdministrationSequenceNumber,
					}),
					state: getResponse(response),
					conditionType: ConditionArea.ELIGIBILITY,
				} as any;
			}
			if (condition.node.code === EligibilityConditionCode.ADMINISTRATION_ADHERENCE_RATE) {
				const props = agreement.eligibilityConditionSetsConnection.edges.find((v) => v.minimumPercentage);
				const eligibilityResponseIndex = (snapshotObject?.eligibilityCodes as PatientEligibilityCodes)?.findIndex(
					(eligibility) => eligibility.code === EligibilityConditionCode.ADMINISTRATION_ADHERENCE_RATE
				);
				const response =
					eligibilityResponseIndex >= 0 &&
					(snapshotObject.eligibilityCodes[eligibilityResponseIndex] as PatientEligibilityCode).response;

				return {
					name: "ADMINISTRATION_ADHERENCE_RATE",
					value: i18next.t("ADMINISTRATION_ADHERENCE_RATE_VALUE", {
						minimum: t("minimum"),
						minimumPercentage: `${props?.minimumPercentage}%`,
						dispensingGap: t("for_duration_of_treatment_up_to"),
						gap: `${props?.days}`,
						days: t("days"),
					}),
					state: getResponse(response),
					conditionType: ConditionArea.ELIGIBILITY,
				} as any;
			}
		})
		.filter((v) => !!v?.state);
}

function getBaseAgreementConditions(conditionStateProps: ConditionStateProps, t: TrFun): Array<ICondition> {
	const { relevantBaseAgreementVersion, initialAgreementVersion, snapshotObject, forms, prescriptionDate } = conditionStateProps;
	const agreementConditions = [
		{
			name: "treatments",
			value: Array.from(new Set(getPreparations(relevantBaseAgreementVersion).map(formatPreparation)))
				.sort()
				.join(", "),
			state: "matched",
			conditionType: ConditionArea.ENROLLMENT,
		},
		{
			name: "diseases",
			value: formatDiseases(relevantBaseAgreementVersion.enrollingConditionSets as unknown as Array<EnrollingConditionSet>, t).join(
				", "
			),
			state: "matched",
			conditionType: ConditionArea.ENROLLMENT,
		},
		{
			name: "indication",
			value: formatIndications(
				relevantBaseAgreementVersion.enrollingConditionSets as unknown as Array<EnrollingConditionSet>,
				t
			).join(", "),
			state: "matched",
			conditionType: ConditionArea.ENROLLMENT,
		},
		...(initialAgreementVersion.isRequiredFormsEnabled
			? [
					{
						name: "requiredForms",
						value: initialAgreementVersion.requiredFormFlexEntityTypes?.map((f) => t(f.localizationKey)).join(", ") || "",
						state: "matched" as ConditionState,
						conditionType: ConditionArea.ENROLLMENT,
					},
			  ]
			: []),
		...(initialAgreementVersion.isAllowedFormsEnabled
			? [
					{
						name: "allowedForms",
						value: initialAgreementVersion.allowedFormFlexEntityTypes?.map((f) => t(f.localizationKey)).join(", ") || "",
						state: "matched" as ConditionState,
						conditionType: ConditionArea.ENROLLMENT,
					},
			  ]
			: []),
		...(relevantBaseAgreementVersion.isCoveringIntervalEnabled && snapshotObject.coverEndDate
			? [
					{
						name: "to-be-covered-before",
						value: format(parseDate(snapshotObject.coverEndDate), "dd.MM.yyyy"),
						state: getEligibilityConditionState(snapshotObject.claimEligibilityStatus),
						conditionType: ConditionArea.ELIGIBILITY,
					},
			  ]
			: []),
		...(relevantBaseAgreementVersion.isCoveringIntervalEnabled && !snapshotObject.coverEndDate
			? [
					{
						name: "to-be-covered",
						state: getEligibilityConditionState(snapshotObject.claimEligibilityStatus),
						conditionType: ConditionArea.ELIGIBILITY,
					},
			  ]
			: []),
		...(snapshotObject.cycleEligibilityStatus !== undefined
			? [
					{
						name: "cycle-adherence",
						state: getEligibilityConditionState(snapshotObject.cycleEligibilityStatus),
						conditionType: ConditionArea.ELIGIBILITY,
					},
			  ]
			: []),
		...(snapshotObject.conditions.length
			? snapshotObject.conditions
					.map((condition) => {
						const relevantAgreementVersion = [relevantBaseAgreementVersion, ...relevantBaseAgreementVersion.nextVersions].find(
							(nextVersion) => nextVersion.id === snapshotObject.agreementVersionId
						);
						const scenarioCondition = relevantAgreementVersion?.scenarios
							.find((s) => s.order === condition.scenarioOrder)
							?.conditions.find((f) => f.order === condition.conditionOrder);
						const isRatingCondition = scenarioCondition?.conditionType === ScenarioConditionType.Rating;

						return (
							scenarioCondition && {
								...formatICondition(
									scenarioCondition,
									t,
									relevantBaseAgreementVersion.dataContainers,
									relevantBaseAgreementVersion.currency
								),
								state:
									!isRatingCondition ||
									isRecordInRatingCoveragePeriod(scenarioCondition as ScenarioCondition, forms, prescriptionDate)
										? getSnapshotConditionState(condition)
										: getSnapshotConditionState({ ...condition, certainty: "failed" }),
							}
						);
					})
					.filter(Boolean)
			: []),
	];
	const eligibilityConditions = getBaseAgreementEligibilityConditions(
		relevantBaseAgreementVersion,
		initialAgreementVersion.eligibilityConditionTreatments || [],
		snapshotObject,
		t
	);
	return [...agreementConditions, ...eligibilityConditions] as any;
}

interface IFormData {
	rating: ConditionConsideredRating | undefined;
	coverageStartDate: Date | null;
	coverageEndDate: Date | null;
	therapyTrialStartDate: Date | null;
	therapyTrialEndDate: Date | null;
}

/**
 * Checks if a records prescription date falls into a coverage period of a form that has the same rating as the scenario condition provided
 *
 * @param {ScenarioCondition} scenarioCondition - The scenario condition containing the rating to be checked
 * @param {IFormsCard[][]} forms - All forms related to the case
 * @param {prescriptionDate} prescriptionDate - The prescription date of the record to be checked
 * @returns {boolean} Returns a boolean, that specifies if the rating is relevant for the provided record
 */
function isRecordInRatingCoveragePeriod(scenarioCondition: ScenarioCondition, forms: IFormsCard[][], prescriptionDate: Date) {
	return !!forms.find((formCardData) => {
		const prescriptionDateAsDate = parseDate(prescriptionDate);
		const formsData = formCardData.map((form) => {
			const parsedFormData = JSON.parse(form.entity?.data || "{}");

			return {
				rating: parsedFormData[AttributeIdentifiers.Rating],
				coverageStartDate:
					parsedFormData[AttributeIdentifiers.CoverageStartDate] &&
					parseDate(parsedFormData[AttributeIdentifiers.CoverageStartDate]),
				coverageEndDate:
					parsedFormData[AttributeIdentifiers.CoverageEndDate] && parseDate(parsedFormData[AttributeIdentifiers.CoverageEndDate]),
				therapyTrialStartDate:
					parsedFormData[AttributeIdentifiers.TherapyTrialStartDate] &&
					parseDate(parsedFormData[AttributeIdentifiers.TherapyTrialStartDate]),
				therapyTrialEndDate:
					parsedFormData[AttributeIdentifiers.TherapyTrialEndDate] &&
					parseDate(parsedFormData[AttributeIdentifiers.TherapyTrialEndDate]),
			} as IFormData;
		});
		const sortedFormData = sortBy(
			formsData,
			byAsc((form) => form.coverageStartDate || form.therapyTrialStartDate)
		);

		return sortedFormData.some((formData, index) => {
			const nextFormDate = formsData[index + 1]?.coverageStartDate;
			const extendedTherapyTrialEndDate =
				formData.therapyTrialEndDate && nextFormDate && differenceInDays(nextFormDate, formData.therapyTrialEndDate) > 1
					? addDays(nextFormDate, -1)
					: formData.therapyTrialEndDate;

			return (
				formData.rating === scenarioCondition.consideredRating &&
				((!!formData.coverageStartDate &&
					isDateMatchedByTimeframe([formData.coverageStartDate, formData.coverageEndDate], prescriptionDateAsDate)) ||
					(!!formData.therapyTrialStartDate &&
						isDateMatchedByTimeframe([formData.therapyTrialStartDate, extendedTherapyTrialEndDate], prescriptionDateAsDate)))
			);
		});
	});
}

function getInvoiceFinancialSummary(
	canSeeFinancials: boolean,
	financialDataPoints: Array<GetCaseByIdQuery["cases"][number]["financialDataPoints"][number]>
) {
	const getAdjustedAmount = (d: any) => {
		if (!d.sourceRefund?.adjustedAmount) {
			return d?.refundable || 0;
		}

		return d.sourceRefund?.adjustedAmount || 0;
	};

	const filteredFinancialDataPoints = financialDataPoints.some((dataPoint) => dataPoint.sourceRefund?.adjustedAmount)
		? financialDataPoints
		: financialDataPoints.filter((dataPoint) => !dataPoint.isDelta);

	return canSeeFinancials
		? {
				refundableAmount: sumBy(filteredFinancialDataPoints, getAdjustedAmount),
				newAmount: sumBy(
					filteredFinancialDataPoints.filter((d) => (d.variable || 0) > 0 || d.sourceRefund?.status === RefundStatus.New),
					getAdjustedAmount
				),
				pendingAmount: sumBy(
					filteredFinancialDataPoints.filter((d) => (d.variable || 0) > 0 || d.sourceRefund?.status === RefundStatus.Pending),
					getAdjustedAmount
				),
				invoicedAmount: sumBy(
					filteredFinancialDataPoints.filter(
						(d) =>
							d.sourceRefund?.status !== RefundStatus.Declined && d.sourceRefund?.documents?.status === DocumentStatus.Issued
					),
					getAdjustedAmount
				),
				settledAmount: sumBy(
					filteredFinancialDataPoints.filter(
						(d) =>
							d.sourceRefund?.status !== RefundStatus.Declined && d.sourceRefund?.documents?.status === DocumentStatus.Settled
					),
					getAdjustedAmount
				),
				declinedAmount: sumBy(
					filteredFinancialDataPoints.filter((d) => d.sourceRefund?.status === RefundStatus.Declined),
					getAdjustedAmount
				),
		  }
		: {
				refundableAmount: PLACEHOLDER_XX,
				newAmount: PLACEHOLDER_XX,
				pendingAmount: PLACEHOLDER_XX,
				invoicedAmount: PLACEHOLDER_XX,
				settledAmount: PLACEHOLDER_XX,
		  };
}

export interface SanitizedTreatmentPrices {
	brandName: string;
	genericName: string | null;
	details: Array<{
		price: number;
		code: string;
		pack: string;
		publicPrice: number;
		exFactoryPrice: number;
	}>;
}

function getSanitizedPriceValue(c: GetCaseByIdQuery["cases"][number], isPackAny: boolean, price: number) {
	if (isPackAny) {
		return EMPTY_VALUE_PLACEHOLDER;
	} else return c?.canUserViewFinancials ? renderCurrency(price, c.baseAgreement?.currency?.Code, 2, 2) : PLACEHOLDER_XX;
}

function reducedTreatments(
	t: NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["treatmentPricesConnection"]["edges"][number][]
) {
	const treatment = t[0];
	if (t.length > 1) {
		return t.reduce((acc: any, current, i) => {
			if (i > 1)
				return {
					...acc,
					details: [
						...acc.details,
						{
							price: current.price,
							exFactoryPrice: current.node.prices.find((price) => price.priceType === PriceCategory.Pexf)?.Price,
							publicPrice: current.node.prices.find((price) => price.priceType === PriceCategory.Ppub)?.Price,
							code: getTreatmentCodeByCountry(current.node) || EMPTY_VALUE_PLACEHOLDER,
							pack: current.node.Name || EMPTY_VALUE_PLACEHOLDER,
						},
					],
				};

			return {
				brandName: acc.node?.regulatedBy.BrandName,
				genericName: acc.node?.regulatedBy.GenericName,
				details: [
					{
						price: acc.price,
						code: getTreatmentCodeByCountry(acc.node) || EMPTY_VALUE_PLACEHOLDER,
						pack: acc.node.Name || EMPTY_VALUE_PLACEHOLDER,
						exFactoryPrice: acc.node.prices.find((price: Price) => price.priceType === PriceCategory.Pexf)?.Price,
						publicPrice: acc.node.prices.find((price: Price) => price.priceType === PriceCategory.Ppub)?.Price,
					},
					{
						price: current.price,
						code: getTreatmentCodeByCountry(current.node) || EMPTY_VALUE_PLACEHOLDER,
						pack: current.node.Name || EMPTY_VALUE_PLACEHOLDER,
						exFactoryPrice: current.node.prices.find((price) => price.priceType === PriceCategory.Pexf)?.Price,
						publicPrice: current.node.prices.find((price) => price.priceType === PriceCategory.Ppub)?.Price,
					},
				],
			};
		});
	}

	return {
		brandName: treatment.node?.regulatedBy.BrandName,
		genericName: treatment.node?.regulatedBy.GenericName,
		details: [
			{
				price: treatment.price,
				code: getTreatmentCodeByCountry(treatment.node) || EMPTY_VALUE_PLACEHOLDER,
				pack: treatment.node.Name || EMPTY_VALUE_PLACEHOLDER,
				exFactoryPrice: treatment.node.prices.find((price) => price.priceType === PriceCategory.Pexf)?.Price,
				publicPrice: treatment.node.prices.find((price) => price.priceType === PriceCategory.Ppub)?.Price,
			},
		],
	};
}

function getRelevantTreatmentPrices(
	c: GetCaseByIdQuery["cases"][number] & {
		flexEntities: Array<GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]["node"]>;
	}
) {
	const agreementVersions = [...c.baseAgreement.nextVersions, c.baseAgreement] as Array<AgreementVersionType>;
	const preparations = c.flexEntities.flatMap((entity) => getStartingPointPreparations(entity.data));
	const treatmentPrices = distinctBy(
		agreementVersions.flatMap((a) => {
			return a.treatmentPricesConnection.edges;
		}),
		(t) => t.node?.Code || ""
	).sort(sortBySelectors((t) => formatTreatment(t.node as Treatment)));

	const isTreatmentRelevantByStartingPointScope = (treatment: (typeof treatmentPrices)[number]["node"]) =>
		preparations.some(
			(preparation) =>
				(preparation.brandName?.Name === anyNamedEntity.Name || preparation.brandName?.Name === treatment.regulatedBy.BrandName) &&
				preparation.genericName?.Name === treatment.regulatedBy.GenericName
		);
	const groupedTreatmentPrices = Array.from(
		groupBy(
			treatmentPrices.filter((treatmentPrice) => isTreatmentRelevantByStartingPointScope(treatmentPrice.node)),
			(agreementTreatmentRelationship) =>
				`${agreementTreatmentRelationship.node.regulatedBy.GenericName}.${agreementTreatmentRelationship.node.regulatedBy.BrandName}`
		).values()
	);
	const sanitizedTreatmentPrices: Array<SanitizedTreatmentPrices> = groupedTreatmentPrices.map((treatmentPrice) =>
		reducedTreatments(treatmentPrice)
	) as unknown as SanitizedTreatmentPrices[];

	const anyGenerics = distinctBy(
		preparations.filter((prep) => prep.brandName.Name === "Any"),
		(preparation) => preparation.genericName.Name
	).map((preparation) => {
		return {
			brandName: preparation.brandName.Name,
			genericName: preparation.genericName.Name,
			details: [{ price: 0, code: EMPTY_VALUE_PLACEHOLDER, pack: "Any", exFactoryPrice: 0, publicPrice: 0 }],
		} as SanitizedTreatmentPrices;
	});

	return anyGenerics.length ? [...sanitizedTreatmentPrices, ...anyGenerics] : sanitizedTreatmentPrices;
}

function canSeeFinancials(c?: Maybe<GetCaseByIdQuery["cases"][number]>) {
	return c?.canUserViewFinancials || false;
}

function createHeaderValues(
	financialDataPoints: Array<GetCaseByIdQuery["cases"][number]["financialDataPoints"][number]>,
	currency: Maybe<NonNullable<GetCaseByIdQuery["cases"][number]["baseAgreement"]>["currency"]>,
	canSeeFinancials: boolean
) {
	const filteredDataPoints = financialDataPoints.filter((dataPoint) => !dataPoint.isDelta);
	const isAdjusted = financialDataPoints.some((fdp) => !!fdp.sourceRefund?.adjustedAmount);
	const payable = isAdjusted
		? sumBy(financialDataPoints || [], (f) => {
				const refund = f.sourceRefund?.adjustedAmount ? f.sourceRefund?.adjustedAmount : f?.refundable;
				if (f.isDelta) return f?.payable || 0 - refund || 0;
				if (!f.sourceRefund?.adjustedAmount) return f?.payable || 0;

				return f.payable + f.refundable - f.sourceRefund?.adjustedAmount;
		  })
		: sumBy(filteredDataPoints || [], (f) => f?.payable || 0);

	return {
		isAdjusted,
		total: canSeeFinancials
			? sumBy(
					filteredDataPoints.filter((dataPoint) => !dataPoint.isDelta) || [],
					(f) => (f?.payable || 0) + (f?.variable || 0) + (f?.refundable || 0)
			  )
			: PLACEHOLDER_XX,
		payable: canSeeFinancials ? payable : PLACEHOLDER_XX,
		variable: canSeeFinancials ? sumBy(financialDataPoints || [], (f) => f?.variable || 0) : PLACEHOLDER_XX,
		...getInvoiceFinancialSummary(canSeeFinancials, financialDataPoints),
		currency,
	};
}

export interface DataParsedFlexEntity {
	createdOn: number;
	data: any;
	__typename: "FlexEntity";
	id: string;
	status: FlexFormStatus | null;
	documentStatus: DocumentStatus | null;
	lastUpdatedOn: any;
	firstPrescriptionDate: any;
	type: GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]["node"]["type"];
}

function getCaseDetailsFieldsValue(
	entity: GetCaseByIdQuery["cases"][number]["flexEntitiesConnection"]["edges"][number]["node"],
	entityNumber: number,
	t: TrFun
): Array<IAttribute> {
	const schema = JSON.parse(entity.type.data) as FlexEntitySchema;
	const fieldControllers = schema.sections.flatMap((section) =>
		section.fields.map((field) => fieldControllerFactory(field, section, field.attribute.identifier))
	);
	const flattenedData = flattenFlexEntitySection(entity.data, schema);
	const attributeIdsInCaseDetails = entity.type.catalogueAttributesConnection.edges
		.filter((e) => e.isDisplayedInCaseDetails)
		.map((e) => e.node.id);

	const fieldsWithData = schema.sections.flatMap((section) =>
		section.fields.map((field) => {
			const identifier = field.attribute.identifier;
			const controller = fieldControllers.find((c) => c.path === identifier)!.getValueTypeProps();

			return {
				attribute: field.attribute,
				value: formatDataPoint(flattenedData[identifier], controller, field.attribute.dataType, t),
				position: parseInt(`${entityNumber}${section.position}${field.row}${field.position}`),
			};
		})
	);

	return fieldsWithData.filter((f) => f.value && attributeIdsInCaseDetails.includes(f.attribute.id));
}

const getIndicationNames = (caseEntity: GetCaseByIdQuery["cases"][number], startingPointData: string, t: TrFun) => {
	if (caseEntity.indication) {
		return [indicationDisplay(caseEntity.indication, t)];
	}

	const parsedData = JSON.parse(startingPointData);

	if (parsedData.indication) {
		return [parsedData.indication.code];
	}

	return null;
};

const scopeFilters = (
	scope: GetCaseByIdQuery["cases"][number]["baseAgreement"]["enrollingConditionSets"][number]["scopesConnection"]["edges"][number]["node"],
	scopeId: string
) =>
	scope.organizationScopesConnection.edges.some((organizationScope) => organizationScope.organizationScopeId === scopeId) ||
	scope.countryScopesConnection.edges.some((countryScope) => countryScope.countryScopeId === scopeId);

const getInformationFromScope = (caseEntity: GetCaseByIdQuery["cases"][number], scopeId: string, t: TrFun) => {
	const enrollingConditionSet = caseEntity.baseAgreement.enrollingConditionSets.find((enrollingConditionSet) =>
		enrollingConditionSet.scopesConnection.edges.some(({ node: scope }) => scopeFilters(scope, scopeId))
	);
	const scope = enrollingConditionSet?.scopesConnection.edges.find(({ node: scope }) => scopeFilters(scope, scopeId))?.node;

	return {
		diseases: scope?.diseases.map((disease) => disease.Icd10Code) || [t(notAvailableKey)],
		indications: scope?.indications.map((indication) => indicationDisplay(indication, t)) || [t(notAvailableKey)],
	};
};

export default function Overview(props: { unitTestId?: string | null }) {
	const { t } = useTranslation();
	const query = useLocationQuery();
	const id = props && props.unitTestId ? props.unitTestId : query.get("id");
	type View = "scenarios" | "similar-cases" | "chats" | "case-event-logs";
	const setAgreementsData = useSetRecoilState(flexibleFormRelatedAgreements);

	const [selectedView, setSelectedView] = useState<View | undefined>();

	function handleCloseDrawer() {
		setSelectedView(undefined);
	}

	function handleToggleOpen(view: View) {
		setSelectedView(view !== selectedView ? view : undefined);
	}

	useSetCaseBreadcrumbs(id || "");

	const setAgreementOrganizationRole = useSetRecoilState(caseAgreementOrganizationRole);

	useEffect(() => {
		return () => setAgreementsData({ loading: false });
	}, []);

	const {
		data: sourceData,
		loading,
		error,
		refetch,
	} = useDataQuery<GetCaseByIdQuery>(GET_CASE_BY_ID, {
		variables: { id },
		skip: !id,
		fetchPolicy: "network-only", // we need this to be 'network-only' as the query cache is updated in CaseReview.tsx with negotiation round updates
		onCompleted: (data: GetCaseByIdQuery) => {
			const startingPoint = data.cases.flatMap((caseEntity) =>
				caseEntity.flexEntitiesConnection.edges.filter((edge) => edge.isStartingPoint).map((edge) => edge.node)
			)[0];
			const deserializedStartingPointData = JSON.parse(startingPoint.data);
			const ignoredAttributes = combineIgnoredAndMissingAttributes(
				startingPoint.type.ignoredAttributes,
				startingPoint.type.catalogueAttributesConnection.edges.flatMap(
					(attributeConnection) => attributeConnection.node
				) as Array<CatalogueAttribute>
			);
			setAgreementsData({
				data: data.cases
					.flatMap((caseEntity) =>
						[caseEntity.baseAgreement as unknown as BaseAgreement].concat(
							caseEntity.baseAgreement?.nextVersions as unknown as Array<BaseAgreement>
						)
					)
					.flatMap((agreement) =>
						agreement.enrollingConditionSets.flatMap((conditionSet) => {
							return conditionSet.scopesConnection.edges
								.filter(
									(scope) =>
										!scope.excludedOn &&
										isConditionSetMatched({
											values: deserializedStartingPointData,
											agreementScope: scope.node,
											conditionSet,
											ignoredAttributes,
										})
								)
								.map((scope) => ({ scope: scope.node, conditionSet, agreement }));
						})
					),
				allMatchedAgreementVersions: data.cases.flatMap((caseEntity) =>
					[caseEntity.baseAgreement as unknown as BaseAgreement].concat(
						caseEntity.baseAgreement?.nextVersions as unknown as Array<BaseAgreement>
					)
				),
				loading: false,
			});
		},
	});
	const data = useMemo(
		() =>
			sourceData && {
				...sourceData,
				cases: sourceData.cases.map((caseEntity) => ({
					...caseEntity,
					flexEntities: caseEntity.flexEntitiesConnection.edges.map(({ node }) => node),
				})),
			},
		[sourceData]
	);

	const isStob =
		data &&
		!!data.cases.length &&
		!!(data.cases[0].baseAgreement?.scenarios ?? [])
			.flatMap((scenario) => scenario.conditions ?? [])
			.find(
				(condition) => condition.conditionType === ConditionType.AdverseEvent || condition.conditionType === ConditionType.Response
			);

	const canUserViewFinancials = !!data && !!data.cases.length && data.cases[0].canUserViewFinancials;

	const mappedData =
		data &&
		data.cases.length &&
		data.cases
			?.map((c) => ({
				...c,
				flexEntities: c.flexEntities.map((entity) => ({
					...entity,
					createdOn: Date.parse(entity.createdOn),
					data: JSON.parse(entity.data),
				})),
			}))
			.map((c) => {
				const flexEntity = c.flexEntitiesConnection.edges.find(({ isStartingPoint }) => isStartingPoint)?.node;
				if (!flexEntity) return undefined;
				const flexEntityData = JSON.parse(flexEntity.data);
				const schema: FlexEntitySchema = JSON.parse(flexEntity.type.data);
				const allFormsOfCurrentCase = c.flexEntities
					.filter(
						(e) =>
							e.status !== FlexFormStatus.Draft &&
							e.type.catalogueAttributesConnection.edges.some(
								(catalogueAttribute) => catalogueAttribute.isDisplayedInCaseDetails
							)
					)
					.sort(sortBySelectors((e) => e.createdOn));
				const customCaseDetailsData = allFormsOfCurrentCase.flatMap((e, i) => getCaseDetailsFieldsValue(e, i, t));
				const patAttributesDefaultSec = schema.sections[0]?.fields?.filter(
					(f) => f.attribute.type === CatalogueAttributeType.Patient
				);
				const legalEntityAttribute = schema.sections[0]?.fields?.find(
					(attribute) => attribute.attribute.identifier === AttributeIdentifiers.LegalEntity
				);

				const patientAttributes = (patAttributesDefaultSec ?? [])
					.map(
						(patientAttr) =>
							({
								value: (JSON.parse(flexEntity.data) as any)?.[patientAttr.attribute.identifier],
								attribute: patientAttr.attribute,
								position: patientAttr.position,
							} as IAttribute)
					)
					.sort(sortBySelectors((e) => e.position));

				const filteredFinancialDataPoints = Array.from(groupBy(c.financialDataPoints, (fdp) => fdp.uuid))
					.flatMap(([_, fdps]) => {
						const validFdps = fdps.filter((fdp) => !fdp.isDeleted || !!fdp.revokedOn);
						const hasDeltas = validFdps.some((fdp) => fdp.isDelta);

						if (hasDeltas) {
							return validFdps
								.filter((fdp) => (fdp.isDelta && fdp.sourceRefund) || (!fdp.isDelta && fdp.isLast))
								.map((fdp) => ({
									...fdp,
									sourceDocumentState: fdp.isDelta
										? fdp.sourceDocumentState || null
										: validFdps.find((validFdp) => !!validFdp.sourceDocumentState && !validFdp.isDelta)
												?.sourceDocumentState || null,
									sourceRefund: fdp.isDelta
										? fdp.sourceRefund || null
										: validFdps.find((validFdp) => !!validFdp.sourceRefund && !validFdp.isDelta)?.sourceRefund || null,
								}));
						}

						return validFdps
							.filter((fdp) => fdp.isLast)
							.map((fdp) => ({
								...fdp,
								sourceDocumentState:
									validFdps.find((validFdp) => !!validFdp.sourceDocumentState && !validFdp.isDelta)
										?.sourceDocumentState || null,
								sourceRefund: validFdps.find((validFdp) => !!validFdp.sourceRefund)?.sourceRefund || null,
							}));
					})
					.filter(({ uuid }) => !!uuid);

				const isPatientNotEnrolledInTreatment = c.flexEntities.some(
					(entity) =>
						entity.status === FlexFormStatus.Submitted && entity.data[AttributeIdentifiers.PatientEnrolledInTreatment] === false
				);
				const hasDiscontinuationReason = c.flexEntities.some(
					(entity) => entity.status === FlexFormStatus.Submitted && !!entity.data[AttributeIdentifiers.ReasonForDiscontinuation]
				);
				const relevantTreatmentPrices = getRelevantTreatmentPrices(c);
				const { indications, diseases } = getInformationFromScope(c, flexEntityData[AttributeIdentifiers.ScopeId], t);
				const diseaseNames = c.disease?.Name ? [c.disease.Name] : diseases;
				const indicationNames = getIndicationNames(c, flexEntity.data, t) ?? indications;
				const allCounterparties = c.baseAgreement.counterpartiesConnection.edges.map((elem) => {
					return { name: elem.node.name, id: elem.node.id };
				});
				const counterpartyInReview = (c.status.toString() === CaseStatus.InReview && c.counterpartyInReview) || "";
				const formData = createFormCardData(c);

				return {
					matchedAgreementVersion: c.baseAgreement,
					canUserViewFinancials: c.baseAgreement.canUserViewFinancials,
					headers: [
						createHeaderValues(
							filteredFinancialDataPoints.filter(
								(financialDataPoint) => !financialDataPoint.revokedOn && !financialDataPoint.isDeleted
							),
							c.baseAgreement?.currency,
							canSeeFinancials(c)
						),
					],
					caseInformation: {
						patientAttributes: patientAttributes,
						caseId: c?.id,
						agreementId: c?.baseAgreement?.id,
						agreementOrganizationRole: c?.baseAgreement?.userOrganizationRole,
						creationDate: c?.creationDate,
						legalEntity: legalEntityAttribute ? c?.legalEntity?.name || EMPTY_VALUE_PLACEHOLDER : undefined,
						isFormOwner: c?.isFormOwner,
						assignedTo: c?.assignedTo,
						relevantIndication: c?.relevantArticle71Indication,
						customCaseDetailsData,
					},
					negotiationRounds: c?.negotiationRounds
						?.map(
							(round) =>
								({
									...round,
									data: JSON.parse(round.data),
								} as TypedNegotiationRound)
						)
						.sort(sortBySelectors((round) => round.source.createdOn)),
					treatmentSummary: {
						indications: indicationNames,
						diseases: diseaseNames,
						drugQuantity: JSON.parse(flexEntity.data).drugsQuantity,
						hasMultipleBasePrices: c.baseAgreement.scenarios.some((scenario) => !!scenario.hasMultipleBasePrices),
						treatments: relevantTreatmentPrices.map((relevantTreatmentPrice) => ({
							brandName: relevantTreatmentPrice.brandName,
							genericName: relevantTreatmentPrice.genericName,
							details: relevantTreatmentPrice.details.map((d) => {
								const isPackAny = d.pack === "Any";

								return {
									pack: d.pack,
									basePrice: getSanitizedPriceValue(c, isPackAny, d.price),
									exFactoryPrice: getSanitizedPriceValue(c, isPackAny, d.exFactoryPrice),
									publicPrice: getSanitizedPriceValue(c, isPackAny, d.publicPrice),
									code: d.code,
								};
							}),
						})),
					},
					forms: formData,
					dataGuidanceForms: createFormDataByDataGuidance(c),
					isFormsCreatable: !(isPatientNotEnrolledInTreatment || hasDiscontinuationReason),
					records: Array.from(
						groupBy(
							filteredFinancialDataPoints.map((fdp) => {
								const snapshot = JSON.parse(fdp.evaluationSnapshot || "{}");

								const conditionProps = {
									relevantBaseAgreementVersion: c.baseAgreement,
									initialAgreementVersion: c.baseAgreement,
									snapshotObject: snapshot,
									forms: formData,
									prescriptionDate: fdp.prescriptionDate,
								};

								return {
									...fdp,
									isRevocable: false,
									claimEligibilityStatus: snapshot?.claimEligibilityStatus,
									conditions: getBaseAgreementConditions(conditionProps, t),
								};
							}),
							(fdp) => fdp.uuid
						)
					),
					currency: c.baseAgreement?.currency || undefined,
					agreementUserRole: c.agreementUserRole,
					organizationId: c.sourceTenantId,
					caseInstance: {
						...c,
						baseAgreement: c.baseAgreement,
					} as unknown as Case,
					counterpartyInReview: counterpartyInReview,
					showCounterparties: allCounterparties.length !== counterpartyInReview.length && counterpartyInReview.length > 0,
					hasDiscontinuationReason: hasDiscontinuationReason,
				};
			});

	useEffect(() => {
		if (!mappedData || !mappedData[0]) return;
		setAgreementOrganizationRole(mappedData[0].caseInformation.agreementOrganizationRole || "payable");
	}, [mappedData]);

	if (!id) return <Typography>{t("provide-valid-id")}</Typography>;

	if (loading)
		return (
			<Box width="100%" height="100vh" display="flex" justifyContent="center" alignItems="center">
				<Loading />
			</Box>
		);

	if (!mappedData || !mappedData[0]) return <NotFound />;
	if (error) return <Typography color="error">Error...{console.error(error)}</Typography>;

	const canShowHeader =
		mappedData &&
		(mappedData[0].caseInstance.status !== CaseStatus.InReview ||
			mappedData[0].caseInstance.negotiationRounds.some((round) => round.status !== NegotiationRoundStatus.InReview));

	return (
		<Box display="flex" width="100%">
			<SubscriptionForUpdates onCompleted={refetch} query={RECORDS_SUBSCRIPTION} />
			<Box width={selectedView ? "62%" : "100%"} style={{ transition: "width 0.3s" }}>
				<Box display="inline-flex" alignItems={"center"} marginBottom={3}>
					<Typography style={{ fontSize: 24, fontWeight: 600, lineHeight: 1 }}>{`${t("case")} ${
						mappedData[0].caseInstance.id
					}`}</Typography>
					<Box marginLeft={3}>
						{(mappedData[0].caseInstance.status === CaseStatus.InReview &&
							((mappedData[0].showCounterparties && (
								<MappedComponent
									component="CaseStatusComponent"
									props={{
										text: t("in-review-of") + " " + mappedData[0].counterpartyInReview,
										status: CaseStatus.InReview,
										ignoreTranslation: true,
									}}
								/>
							)) || <MappedComponent component="CaseStatusComponent" props={{ status: CaseStatus.InReview }} />)) ||
							(mappedData[0].caseInstance.status === CaseStatus.Suspended && (
								<MappedComponent
									component="CaseStatusComponent"
									props={{
										text: mappedData[0].hasDiscontinuationReason
											? t("suspended-due-to-therapy-discontinuation")
											: t("suspended-due-to-inactivity"),
										status: CaseStatus.Suspended,
										ignoreTranslation: true,
									}}
								/>
							)) || (
								<MappedComponent
									component="CaseStatusComponent"
									props={{
										status: mappedData[0].caseInstance.status,
									}}
								/>
							)}
					</Box>
				</Box>
				{canShowHeader && <Header isDrawerOpen={!!selectedView} data={mappedData} />}
				<Body
					negotiationRounds={mappedData[0]?.negotiationRounds}
					caseInformation={mappedData?.[0]?.caseInformation as any}
					treatmentSummary={mappedData?.[0]?.treatmentSummary as any}
					forms={mappedData?.[0]?.forms as any}
					isFormsCreatable={mappedData?.[0].isFormsCreatable}
					dataGuidanceForms={mappedData?.[0]?.dataGuidanceForms as any}
					records={mappedData?.[0].records}
					currency={mappedData && mappedData[0].currency}
					isStob={isStob}
					role={mappedData && mappedData[0].agreementUserRole}
					organizationId={mappedData && mappedData[0].organizationId!}
					caseInstance={mappedData && mappedData[0].caseInstance}
					isDrawerOpen={!!selectedView}
					canUserViewFinancials={canUserViewFinancials}
					refetch={refetch}
				/>
			</Box>
			<Box>
				<RightPermanentDrawer
					agreement={mappedData?.[0]?.matchedAgreementVersion}
					agreementUserRole={mappedData?.[0]?.agreementUserRole || null}
					canUserViewFinancials={mappedData?.[0]?.canUserViewFinancials || false}
					caseId={mappedData?.[0]?.caseInformation.caseId}
					selectedView={selectedView}
					handleCloseDrawer={handleCloseDrawer}
					handleToggleOpen={handleToggleOpen}
				/>
			</Box>
		</Box>
	);
}
